import React, { Fragment, useCallback, useContext, useState } from "react"
import { Accordion, Button, Grid, Header, Icon, Image, Select } from "semantic-ui-react"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"
import Site, { SitePlaceholder } from "./Site"
import { CampingStyleProps, CampProps, SiteProps } from "../../interfaces"
import { validRigType, validPrerequisiteKind, validElectric } from "~/types"
import { CAMPING_STYLES, PREREQUISITES } from "~/constants"
import { isMobile } from "react-device-detect"
import electricOptions from "./prerequisites/electricOptions"
import sortSites from "../../utils/sortSites"
import { BookerContext } from "~/contexts/BookerContext"
import { ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from "@heroicons/react/24/outline"
import createMarkup from "../../utils/createMarkup"
import RigTypeField from "./SiteFields/RigTypeField"
import formatLengthUnit from "~/utils/formatLengthUnit"
import { MapIcon } from "@heroicons/react/24/solid"
import { Dialog, Transition } from "@headlessui/react"
import NoAvailableSites from "./NoAvailableSites"
import { Metadata } from "~/api2/types"

const removeQueryParam = (param: string) => {
  // Use URLSearchParams to manipulate query parameters
  const urlParams = new URLSearchParams(window.location.search)
  urlParams.delete(param)

  // Create new URL object
  const url = new URL(window.location as unknown as string)
  url.search = urlParams.toString()

  // Use history API to update the URL without reloading
  window.history.pushState({}, "", url.toString())
}

interface ComponentProps {
  setActiveSite: (v: SiteProps) => void
  camp: CampProps
  userCampingStyle: CampingStyleProps
  nights: number
  sites: SiteProps[]
  paginationMeta: Metadata
  setShowDatesCard
  loading: boolean
  notMatchAmperage: boolean
  userRigType: validRigType
  setUserRigType: (v: validRigType) => any
  userRigLength: number
  setUserRigLength
  userAmps: validElectric
  setUserAmps
  preSelectedSite: SiteProps
  setPreSelectedSite: (v: SiteProps) => void
  openedSite: SiteProps
  setOpenedSite: (v: SiteProps) => void
  setSitePhotos: React.Dispatch<React.SetStateAction<Record<string, string[]>>>
  sitePhotos: Record<string, string[]>

  page?: number
  setPage?: React.Dispatch<React.SetStateAction<number>>
  perPage?: number
}

const Sites = (props: ComponentProps) => {
  const sites: SiteProps[] = props.sites
  const [isActive, setIsActive] = useState(false)

  const { groupedReservationId, setGroupedReservationId, campingStyle } = useContext(BookerContext)

  const isBoat = props.userCampingStyle.name === CAMPING_STYLES.BOAT_SLIP
  const suffix = isBoat
    ? formatLengthUnit(props.camp.length_unit) + " Boat"
    : formatLengthUnit(props.camp.length_unit) + " Rig"
  const isMapPresent = campingStyle.map_url || props.camp.map_url

  let rigLengthOptions: Array<{ key: number; value: number; text: string }> = []

  if (isBoat) {
    rigLengthOptions = [null].concat(Array.from(new Array(34), (x, i) => i + 5)).map((x) => {
      return { key: x, value: x, text: x ? `${x}${suffix}` : "" }
    })
  } else {
    rigLengthOptions = Array.from(new Array(59), (x, i) => i + 12).map((x) => {
      return { key: x, value: x, text: x ? `${x}${suffix}` : "" }
    })
  }

  const isFilteringFor = (kind: validPrerequisiteKind): boolean => {
    const requiresRigLength = props.camp.prerequisites.map((pr) => pr.kind).includes(kind)

    let isFilterValuePresent

    if (kind == PREREQUISITES.MAX_RIG_LENGTH) isFilterValuePresent = !!props.userRigLength
    else if (kind == PREREQUISITES.ELECTRIC) isFilterValuePresent = !!props.userAmps
    else if (kind == PREREQUISITES.RIG_TYPE) isFilterValuePresent = !!props.userRigType

    return requiresRigLength && isFilterValuePresent
  }

  const renderSites = useCallback(
    () =>
      sites
        .filter((site) => site.is_available)
        .sort((a, b) => sortSites(a, b, props.userCampingStyle))
        .map((site, i) => {
          return (
            <Site
              key={site.id}
              nights={props.nights}
              site={site}
              isAvailable={site.is_available}
              setActiveSite={props.setActiveSite}
              userElectric={props.userAmps}
              selectedSite={props.preSelectedSite}
              setSelectedSite={props.setPreSelectedSite}
              openedSite={props.openedSite}
              setOpenedSite={props.setOpenedSite}
              sitePhotos={props.sitePhotos}
              setSitePhotos={props.setSitePhotos}
              dataTestId={`camps-site-selector-${i}`}
              index={i}
            />
          )
        }),
    [
      sites,
      props.nights,
      props.userCampingStyle,
      props.preSelectedSite,
      props.openedSite,
      props.sitePhotos,
      props.setActiveSite,
      props.userAmps,
    ],
  )

  const renderedMap = !props.camp.is_zoom_enabled ? (
    <Image
      src={props.userCampingStyle.map_url || props.camp.map_url}
      style={{ objectFit: "contain", height: isMobile ? "100%" : "auto" }}
      alt="No map available."
      fluid
    />
  ) : (
    <TransformWrapper>
      <TransformComponent
        wrapperStyle={{ width: "100%", height: "100%" }}
        contentStyle={{ width: "100%", height: "100%" }}
      >
        <Image
          className="border h-[200px] w-[200px]"
          src={props.userCampingStyle.map_url || props.camp.map_url}
          alt="No map available."
          fluid
          style={{ objectFit: "contain", height: isMobile ? "100%" : "auto" }}
        />
      </TransformComponent>
    </TransformWrapper>
  )

  const sitePlaceholder = (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <SitePlaceholder />
      ))}
    </>
  )

  const NoAmpMatchNote = () => {
    if (isFilteringFor(PREREQUISITES.ELECTRIC) && props.notMatchAmperage)
      return (
        <div
          style={{ backgroundColor: "#fafafa" }}
          className={`text-black text-lg flex p-4 mb-1 mt-2 border border-gray-900 rounded-md cursor-default`}
        >
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
          <div>
            There are no sites available for these dates that match your exact requirements. Please review the
            alternative options below, or adjust your date selection.
          </div>
        </div>
      )
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {props.userCampingStyle.prerequisites.length > 0 ? (
          <>
            <span className="font-semibold flex-shrink-0">Sites for</span>
            {isFilteringFor(PREREQUISITES.RIG_TYPE) && (
              <div className="">
                <RigTypeField
                  userRigType={props.userRigType}
                  handleRigTypeChange={(e, { value }) => props.setUserRigType(value)}
                  label={false}
                />
              </div>
            )}
            {isFilteringFor(PREREQUISITES.MAX_RIG_LENGTH) && (
              <div className="w-44">
                <Select
                  fluid
                  className="sites-filter-dropdown"
                  options={rigLengthOptions}
                  value={Number(props.userRigLength)}
                  onChange={(e, { value }) => props.setUserRigLength(value)}
                />
              </div>
            )}
            {isFilteringFor(PREREQUISITES.ELECTRIC) && (
              <div className="w-44">
                <Select
                  fluid
                  className="sites-filter-dropdown"
                  options={electricOptions(
                    props.camp.sites.filter((site) => site.camping_style_id == props.userCampingStyle.id),
                  )}
                  value={props.userAmps}
                  onChange={(e, { value }) => props.setUserAmps(value)}
                />
              </div>
            )}
          </>
        ) : (
          <b>Map</b>
        )}
      </div>

      <Grid stackable columns={isMapPresent ? 2 : 1} style={{ marginBottom: 65 }}>
        {renderSites().length > 0 || props.loading ? (
          <>
            {isMapPresent && (
              <div
                onClick={() => setIsActive(true)}
                className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 md:hidden cursor-pointer"
              >
                <div className="relative">
                  <div className="absolute z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-2 text-white font-semibold [&_svg]:w-6 [&_svg]:h-6">
                    <MapIcon className="text-white" />{" "}
                    <span className="text-md whitespace-nowrap text-white">View Map</span>
                  </div>
                  <div
                    style={{
                      backgroundImage: `url(${props.userCampingStyle.map_url || props.camp.map_url})`,
                      backgroundSize: "200%",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      filter: "brightness(0.5)",
                    }}
                    className=" flex gap-3 items-center  rounded-full  px-6 py-2 text-white font-semibold [&_svg]:w-6 [&_svg]:h-6"
                  >
                    <MapIcon className="text-transparent" />{" "}
                    <span className="text-md whitespace-nowrap text-transparent">View Map</span>
                  </div>
                </div>
              </div>
            )}

            <Grid.Column only="mobile" id="columnStyle">
              {props.userCampingStyle.notes && (
                <div className="text-black text-lg flex p-4 mb-6 bg-white border border-gray-900 rounded-md cursor-default">
                  <InformationCircleIcon className="w-5 h-5 text-gray-600 mr-2 mt-1 flex-shrink-0" />
                  <div dangerouslySetInnerHTML={createMarkup(props.userCampingStyle.notes)} />
                </div>
              )}
              <NoAmpMatchNote />

              <div className="mt-4 md:-mt-4 flex flex-col gap-1.5">
                {props.loading ? sitePlaceholder : renderSites()}
              </div>
              <div className="mt-3.5 flex flex-col gap-1.5 mb-20">
                {!props.loading &&
                  props.page &&
                  props.perPage &&
                  props.paginationMeta &&
                  props.paginationMeta?.total_count > props.perPage && (
                    <div className="flex">
                      <Button
                        disabled={props.page === 1}
                        content={`Show previous ${props.perPage} sites`}
                        onClick={() => {
                          window.scrollTo(0, 0)
                          if (props.setPage) props.setPage((props.page || 0) - 1)
                        }}
                      />

                      <Button
                        disabled={renderSites().length < props.perPage}
                        content={`Show next ${props.perPage} sites`}
                        onClick={() => {
                          window.scrollTo(0, 0)
                          if (props.setPage) props.setPage((props.page || 0) + 1)
                        }}
                      />
                    </div>
                  )}
              </div>
            </Grid.Column>
            <Grid.Column only="tablet computer">
              {props.userCampingStyle.notes && (
                <div className="mt-2 text-black text-lg flex p-4 -mb-1 bg-white border border-gray-900 rounded-md cursor-default">
                  <InformationCircleIcon className="w-5 h-5 text-gray-600 mr-2 mt-1 flex-shrink-0" />
                  <div dangerouslySetInnerHTML={createMarkup(props.userCampingStyle.notes)} />
                </div>
              )}
              <NoAmpMatchNote />
              <div className="mt-3.5 flex flex-col gap-1.5">
                {props.loading ? sitePlaceholder : renderSites()}
                {!props.loading &&
                  props.page &&
                  props.perPage &&
                  props.paginationMeta &&
                  props.paginationMeta?.total_count > props.perPage && (
                    <div className="flex">
                      <Button
                        disabled={props.page === 1}
                        content={`Show previous ${props.perPage} sites`}
                        onClick={() => {
                          window.scrollTo(0, 0)
                          if (props.setPage) props.setPage((props.page || 0) - 1)
                        }}
                      />

                      <Button
                        content={`Show next ${props.perPage} sites`}
                        disabled={renderSites().length < props.perPage}
                        onClick={() => {
                          window.scrollTo(0, 0)
                          if (props.setPage) props.setPage((props.page || 0) + 1)
                        }}
                      />
                    </div>
                  )}
              </div>
            </Grid.Column>
          </>
        ) : (
          <NoAvailableSites
            setShowDatesCard={props.setShowDatesCard}
            groupedReservationId={groupedReservationId}
            setGroupedReservationId={setGroupedReservationId}
          />
        )}
        {isMapPresent && (
          <Grid.Column only="tablet computer" className="ml-auto">
            <div className="mt-4">{renderedMap}</div>
          </Grid.Column>
        )}
      </Grid>
      <Transition.Root show={isActive} as={Fragment}>
        <Dialog as="div" className="relative z-[1000]" onClose={() => setIsActive(false)}>
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="pointer-events-none fixed inset-y-0 right-0 flex  pt-20 bg-black/50"
                style={{ marginTop: "0px" }}
              >
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-y-full"
                  enterTo="translate-y-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-y-0"
                  leaveTo="translate-y-full"
                >
                  <Dialog.Panel className="relative rounded-t-lg pointer-events-auto w-screen bg-white pt-10">
                    <XCircleIcon
                      onClick={() => {
                        setIsActive(false)
                      }}
                      className="absolute z-50 right-2 top-2 w-8 h-8 mr-2 flex-shrink-0"
                    />
                    <div className="rounded-lg h-[100%] py-10 px-6 flex justify-center">{renderedMap}</div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default Sites
